// react
import React from 'react';

// mui
import {
  Avatar,
  Box,
  Button,
  Container,
  Divider,
  Stack,
  Typography,
} from '@mui/material';

// templates
import { ModalPageTemplate } from '@/pwa/templates/ModalPageTemplate/ModalPageTemplate';
import { PageTemplate } from '@/pwa/templates/PageTemplate/PageTemplate';

// atoms
import { PageHeader } from '@/pwa/atoms/PageHeader/PageHeader';
import { ColourSwatch } from '@/pwa/atoms/ColourSwatch/ColourSwatch';
import { AvatarOptionThumbnail } from '@/pwa/atoms/AvatarOptionThumbnail/AvatarOptionThumbnail';

// types
import {
  AvatarStyleDefinition,
  AvatarCategoryGroup,
} from '@/pwa/utils/avatarStyles';

export interface AvatarEditorPageProps {
  styles: AvatarStyleDefinition[];
  activeStyleId: string;
  onStyleChange: (styleId: string) => void;
  previewUrl: string;
  categoryGroups: AvatarCategoryGroup[];
  currentOptions: Record<string, string>;
  onOptionChange: (key: string, value: string) => void;
  buildThumbnailUrl: (categoryKey: string, optionValue: string) => string;
  buildStyleSampleUrl: (styleId: string) => string;
  onBack?: () => void;
  onSave: () => void;
  onRandomise: () => void;
  isSaving?: boolean;
}

/**
 * AvatarEditorPage provides a full avatar customisation experience with
 * style switching, live preview, and category-based option selection.
 */
export const AvatarEditorPage = ({
  styles,
  activeStyleId,
  onStyleChange,
  previewUrl,
  categoryGroups,
  currentOptions,
  onOptionChange,
  buildThumbnailUrl,
  buildStyleSampleUrl,
  onBack,
  onSave,
  onRandomise,
  isSaving = false,
}: AvatarEditorPageProps) => {
  return (
    <ModalPageTemplate
      onBack={onBack}
      footerSlot={
        <Box
          sx={{
            bgcolor: 'background.default',
          }}
        >
          <Divider />
          <Container maxWidth='md'>
            <Box
              sx={{
                py: 2,
              }}
            >
              <Button
                variant='contained'
                onClick={onSave}
                disabled={isSaving}
                fullWidth
              >
                {isSaving ? 'Saving...' : 'Save Avatar'}
              </Button>
            </Box>
          </Container>
        </Box>
      }
    >
      <PageTemplate
        headerSlot={<PageHeader title='Avatar Editor' />}
        headerMaxWidth='md'
        maxWidth={false}
        disableGutters
        disablePaddingBottom
        contentSlot={
          <Stack
            sx={{
              gap: 3,
            }}
          >
            {/* Live Preview + divider — sticky so user can see changes while scrolling */}
            <Box
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 2,
                bgcolor: 'background.default',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  py: 2,
                }}
              >
                <Avatar src={previewUrl} sx={{ width: 160, height: 160 }} />
              </Box>
              <Divider />
            </Box>

            {/* Category Sections — grouped with colour swatches above their component */}
            <Container maxWidth='md'>
              <Stack
                sx={{
                  gap: 3,
                }}
              >
                <Button variant='outlined' onClick={onRandomise} fullWidth>
                  Randomise
                </Button>

                {categoryGroups.map((group) => (
                  <Stack
                    key={group.component.key}
                    sx={{
                      gap: 1.5,
                    }}
                  >
                    <Typography variant='subtitle2'>
                      {group.component.label}
                    </Typography>

                    {group.component.type === 'colour' ? (
                      <Stack
                        direction='row'
                        sx={{
                          gap: 1,
                          overflowX: 'auto',
                          p: 0.5,
                          m: -0.5,
                        }}
                      >
                        {group.component.options.map((colour) => (
                          <ColourSwatch
                            key={colour}
                            colour={colour}
                            isSelected={
                              currentOptions[group.component.key] === colour
                            }
                            onClick={() =>
                              onOptionChange(group.component.key, colour)
                            }
                          />
                        ))}
                      </Stack>
                    ) : (
                      <>
                        <Stack
                          direction='row'
                          sx={{
                            gap: 1,
                            overflowX: 'auto',
                            p: 0.5,
                            m: -0.5,
                          }}
                        >
                          {group.component.options.map((option) => (
                            <AvatarOptionThumbnail
                              key={option}
                              src={buildThumbnailUrl(
                                group.component.key,
                                option,
                              )}
                              isSelected={
                                currentOptions[group.component.key] === option
                              }
                              onClick={() =>
                                onOptionChange(group.component.key, option)
                              }
                              alt={option}
                            />
                          ))}
                        </Stack>

                        {/* Attached colour sub-categories */}
                        {group.colours.map((colourCat) => (
                          <Stack
                            key={colourCat.key}
                            direction='row'
                            sx={{
                              gap: 1,
                              overflowX: 'auto',
                              p: 0.5,
                              m: -0.5,
                            }}
                          >
                            {colourCat.options.map((colour) => (
                              <ColourSwatch
                                key={colour}
                                colour={colour}
                                isSelected={
                                  currentOptions[colourCat.key] === colour
                                }
                                onClick={() =>
                                  onOptionChange(colourCat.key, colour)
                                }
                              />
                            ))}
                          </Stack>
                        ))}
                      </>
                    )}
                  </Stack>
                ))}
              </Stack>
            </Container>
          </Stack>
        }
      />
    </ModalPageTemplate>
  );
};

AvatarEditorPage.displayName = 'AvatarEditorPage';
export default AvatarEditorPage;
